import React from 'react';
import { cn } from '@/app/lib/utils';
import { Skeleton } from './Skeleton';

export type TableSkeletonVariant =
  | 'checkbox'
  | 'index'
  | 'text'
  | 'pill'
  | 'avatarText'
  | 'iconText'
  | 'logoText'
  | 'date'
  | 'actions'
  | 'empty';

export interface TableSkeletonConfig {
  variant?: TableSkeletonVariant;
  width?: string | number;
}

interface TableSkeletonCellProps {
  config?: TableSkeletonConfig;
  rowIndex?: number;
  className?: string;
  isActionColumn?: boolean;
}

const fallbackWidths = ['60%', '48%', '68%'];

export const TableSkeletonCell = ({
  config,
  rowIndex = 0,
  className,
  isActionColumn = false,
}: TableSkeletonCellProps) => {
  const variant = config?.variant ?? (isActionColumn ? 'actions' : 'text');
  const width = config?.width ?? fallbackWidths[rowIndex % fallbackWidths.length];

  if (variant === 'empty') return null;

  if (variant === 'checkbox') {
    return <Skeleton className={cn('mx-auto size-4 rounded', className)} />;
  }

  if (variant === 'index') {
    return <Skeleton className={cn('mx-auto h-4 w-3', className)} />;
  }

  if (variant === 'pill') {
    return <Skeleton className={cn('h-5 w-16 rounded-full', className)} />;
  }

  if (variant === 'avatarText') {
    return (
      <div className={cn('flex min-w-0 items-center gap-2', className)}>
        <Skeleton className="size-8 shrink-0 rounded-full" />
        <Skeleton className="h-4" width={width} />
      </div>
    );
  }

  if (variant === 'date') {
    return (
      <div className={cn('flex min-w-0 items-center gap-2', className)}>
        <Skeleton className="size-8 shrink-0 rounded-full" />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <Skeleton className="h-3.5" width={width} />
          <Skeleton className="h-3 w-2/5" />
        </div>
      </div>
    );
  }

  if (variant === 'logoText') {
    return (
      <div className={cn('flex min-w-0 items-center gap-2', className)}>
        <Skeleton className="size-8 shrink-0 rounded-md" />
        <Skeleton className="h-4" width={width} />
      </div>
    );
  }

  if (variant === 'iconText') {
    return (
      <div className={cn('flex min-w-0 items-center gap-2', className)}>
        <Skeleton className="size-4 shrink-0" />
        <Skeleton className="h-4" width={width} />
      </div>
    );
  }

  if (variant === 'actions') {
    return (
      <div className={cn('flex w-full justify-end', className)}>
        <Skeleton className="size-5 rounded" />
      </div>
    );
  }

  return <Skeleton className={cn('h-4', className)} width={width} />;
};

export interface TableSkeletonRowCell {
  id: string;
  config?: TableSkeletonConfig;
  className?: string;
  style?: React.CSSProperties;
  isActionColumn?: boolean;
}

interface TableSkeletonRowProps {
  cells: TableSkeletonRowCell[];
  rowIndex?: number;
  className?: string;
}

export const TableSkeletonRow = ({ cells, rowIndex = 0, className }: TableSkeletonRowProps) => (
  <tr
    className={cn(
      'relative h-11 min-h-11 border-b border-border bg-background divide-x divide-border',
      className
    )}
  >
    {cells.map(cell => (
      <td
        key={cell.id}
        className={cn(
          'h-11 min-h-11 overflow-hidden whitespace-nowrap px-4 py-2 align-middle',
          cell.isActionColumn && 'action-column',
          cell.className
        )}
        data-action-column={cell.isActionColumn ? 'true' : undefined}
        style={cell.style}
      >
        <TableSkeletonCell
          config={cell.config}
          rowIndex={rowIndex}
          isActionColumn={cell.isActionColumn}
        />
      </td>
    ))}
  </tr>
);
